import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { DateTime } from "luxon";
import _ from "lodash";
import { AiOutlineCalendar, AiOutlineClockCircle, AiOutlineShoppingCart } from "react-icons/ai";

import { Heading } from "../component/Heading";
import DatePicker from "../component/DatePicker";
import { Button, Modal } from "react-bootstrap";
import { useCartContext } from "../context/cartContext";
import { server } from "../server";

// ─── Types ───────────────────────────────────────────────────────────────────

type Shop = {
    id: number;
    attributes: {
        name: string;
        district: string;
        address1?: string;
        address2?: string;
        brand_id?: number;
    };
};

type TimeSlot = {
    dates: string[];
    startAt: string;
    endAt: string;
    interval: number;
};

type MenuDetail = {
    title?: string;
    price?: number;
    user_max_quantity?: number;
    user_max_checkout_quantity?: number;
    enable_inventory_control?: boolean;
    show_remaining_quantity?: boolean;
    booking_restriction?: { enabled: boolean; advance_days: number };
    time_slots?: TimeSlot[];
    shops?: { data?: Shop[] };
    date_str?: string;
    terms?: string;
};

type InventoryConfig = {
    time_start: string;
    max_quantity: number;
    used_quantity: number;
    attributes?: { used_quantity?: number; max_quantity?: number };
};


// ─── Constants ───────────────────────────────────────────────────────────────

const districtMap: Record<string, string> = {
    kowloon: "九龍",
    newterritories: "新界",
    hkisland: "香港島",
    Other: "其他",
};

const DAY_MAP = ["日", "一", "二", "三", "四", "五", "六"];

// ─── Helpers ─────────────────────────────────────────────────────────────────

export const timeToNumber = (timeString?: string | null): number => {
    if (_.isNil(timeString)) return 0;
    const hour = Number(timeString.slice(0, 2));
    const minutes = Number(timeString.slice(3));
    return 60 * hour + minutes;
};

const numberToTime = (timeNumber: number): string => {
    const hour = Math.floor(timeNumber / 60);
    const minutes = timeNumber % 60;
    return `${_.padStart(String(hour), 2, "0")}:${_.padStart(String(minutes), 2, "0")}`;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
    label: {
        paddingLeft: 8,
        color: "#0E0F0F",
        fontSize: 16,
        fontWeight: "500",
    } as React.CSSProperties,
    input: {
        borderRadius: 8,
        border: "2px solid #CDCFD0",
        width: "100%",
        marginTop: 8,
        fontSize: 16,
        padding: "8px 16px",
        outline: "none",
        backgroundColor: "white",
        boxSizing: "border-box",
        // maxWidth: 500,
    } as React.CSSProperties,
    select: {
        borderRadius: 8,
        border: "2px solid #CDCFD0",
        width: "100%",
        cursor: "pointer",
        // marginTop: 8,
        // fontSize: 16,
        // padding: "8px 16px",
        // outline: "none",
        backgroundColor: "white",
        // // appearance: "auto",
        // boxSizing: "border-box",
        // WebkitAppearance: "none",
        // MozAppearance: "none",
        // appearance: "none",
        // // maxWidth: 500
    } as React.CSSProperties,
};
const CONTENT_MAX_WIDTH = 760;
// ─── CalendarModal ────────────────────────────────────────────────────────────

type CalendarModalProps = {
    visible: boolean;
    close: () => void;
    setDate: (d: { dayString: string } | null) => void;
    date: {
        dayString: string;
    } | null;
    minDate?: string;
    maxDate?: string;
    availableDates: string[];
};

function CalendarModal({ visible, close, setDate, date, availableDates }: CalendarModalProps) {
    const [selected, setSelected] = useState<string | null>(date?.dayString ?? null);

    const setToday = () => {
        const todayISO = DateTime.now().setZone("Asia/Hong_Kong").toISODate();
        if (todayISO && availableDates.includes(todayISO)) {
            setSelected(todayISO);
        }
    };
    useEffect(() => {
        if (!visible) return;
        setSelected(date?.dayString ?? null);
    }, [visible, date?.dayString]);
    if (!visible) return null;

    return (
        <Modal show={visible} onHide={close} centered>
            <Modal.Header closeButton>
                {/* <Modal.Title>choose date</Modal.Title> */}
            </Modal.Header>
            <Modal.Body>
                <DatePicker
                    setDate={setSelected}
                    selectedDate={selected}
                    availableDates={availableDates}
                // onChange={(dates) => {
                //   if (dates.length) setDate({ dayString: dates[0].toISOString().split("T")[0] });
                // }}
                />
            </Modal.Body>
            <Modal.Footer className="d-flex flex-column m-auto w-100" >
                <div
                    style={{ display: "flex", gap: 8, marginBottom: 8, flexDirection: "row", alignItems: "center", width: "100%" }}>
                    <button
                        type="button"
                        onClick={() => setSelected(null)}
                        style={{
                            flex: 1,
                            padding: "4px 10px",
                            border: "1px solid #E5E7EB",
                            backgroundColor: "white",
                            borderRadius: 4,
                            cursor: "pointer",
                            boxShadow: "0 1px 2.22px rgba(0,0,0,0.22)",
                        }}
                    >
                        清除
                    </button>
                    <button
                        type="button"
                        onClick={setToday}
                        style={{
                            flex: 1,
                            padding: "4px 10px",
                            border: "1px solid #E5E7EB",
                            backgroundColor: "white",
                            borderRadius: 4,
                            cursor: "pointer",
                            boxShadow: "0 1px 2.22px rgba(0,0,0,0.22)",
                        }}
                    >
                        今日
                    </button>
                </div>

                {/* Cancel / Confirm */}
                <div style={{ display: "flex", gap: 8, marginBottom: 8, flexDirection: "row", alignItems: "center", width: "100%" }}>
                    <button
                        type="button"
                        onClick={() => {
                            setSelected(null)
                            close()
                        }}
                        style={{
                            flex: 1,
                            padding: "8px 10px",
                            border: `1px solid ${"var(--bs-primary)"}`,
                            backgroundColor: "white",
                            borderRadius: 20,
                            cursor: "pointer",
                            color: "var(--bs-primary)",
                        }}
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (selected) setDate({ dayString: selected });
                            else setDate(null);
                            close()
                        }}
                        style={{
                            flex: 1,
                            padding: "8px 10px",
                            border: `1px solid ${"var(--bs-primary)"}`,
                            backgroundColor: "var(--bs-primary)",
                            borderRadius: 20,
                            cursor: "pointer",
                            color: "white",
                        }}
                    >
                        確定
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

// ─── QuantityButton ───────────────────────────────────────────────────────────

type QuantityButtonProps = {
    quantity: number;
    setQuantity: (n: number) => void;
    addDisabled?: boolean;
};

function QuantityButton({ quantity, setQuantity, addDisabled }: QuantityButtonProps) {
    const minusDisabled = quantity <= 1;
    const btnStyle: React.CSSProperties = {
        width: 36,
        height: 36,
        border: `1px solid ${"var(--bs-primary)"}`,
        backgroundColor: "white",
        color: "var(--bs-primary)",
        borderRadius: 4,
        fontSize: 20,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    };
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 8 }}>
            <Button type="button" style={{ ...btnStyle, opacity: minusDisabled ? 0.4 : 1, cursor: minusDisabled ? 'not-allowed' : 'pointer' }} disabled={minusDisabled} onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</Button>
            <span style={{ fontSize: 18, fontWeight: "700", minWidth: 24, textAlign: "center" }}>{quantity}</span>
            <Button type="button" style={{ ...btnStyle, opacity: addDisabled ? 0.4 : 1, cursor: addDisabled ? 'not-allowed' : 'pointer' }} disabled={addDisabled} onClick={() => setQuantity(quantity + 1)}>+</Button>
        </div>
    );
}

// ─── Toast ────────────────────────────────────────────────────────────────────

type ToastState = { message: string; type: "success" | "error" } | null;

function ToastMessage({ toast, onClose }: { toast: ToastState; onClose: () => void }) {
    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(onClose, 3000);
        return () => clearTimeout(t);
    }, [toast, onClose]);

    if (!toast) return null;
    return (
        <div style={{
            position: "fixed",
            bottom: 100,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: toast.type === "success" ? "#22c55e" : "#ef4444",
            color: "white",
            padding: "12px 24px",
            borderRadius: 8,
            zIndex: 9999,
            fontSize: 14,
            fontWeight: "600",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            whiteSpace: "nowrap",
        }}>
            {toast.message}
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function OrderSet() {
    const navigate = useNavigate();
    const location = useLocation();
    const state = (location.state ?? {}) as {
        setMenu: MenuDetail; id?: number; counting: number;
    };
    const { company: companyParam } = useParams();
    const menuDetail = useMemo(() => state.setMenu, [state.setMenu]);
    const id = state.id ?? null;
    const counting = state.counting;

    const [toast, setToast] = useState<ToastState>(null);
    const showToast = (message: string, type: "success" | "error") => setToast({ message, type });

    const shopList = useMemo(() =>
        _.map(menuDetail?.shops?.data, (v) => ({
            label: v?.attributes?.name,
            value: v?.id,
            district: v?.attributes?.district,
        })),
        [menuDetail?.shops]
    );

    const districts = useMemo(() =>
        _.uniqBy(
            _.map(menuDetail?.shops?.data, (v) => ({
                label: districtMap[v?.attributes?.district] ?? v?.attributes?.district,
                value: v?.attributes?.district,
            })),
            "value"
        ),
        [menuDetail?.shops]
    );

    const [selectedDistrict, setSelectedDistrict] = useState("");
    const [selectedShop, setSelectedShop] = useState<number | null>(null);
    const [filteredShops, setFilteredShops] = useState(shopList);
    const [date, setDate] = useState<{ dayString: string } | null>(null);
    const [time, setTime] = useState("");
    const [visible, setVisible] = useState(false);
    const [remark, setRemark] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [inventoryStats, setInventoryStats] = useState<InventoryConfig[] | null>(null);
    const [dynamicPriceData, setDynamicPriceData] = useState<any>(null);
    const [currentPrice, setCurrentPrice] = useState<number | undefined>(menuDetail?.price);
    const [isDynamicPrice, setIsDynamicPrice] = useState(false);
    const [disabledDates, setDisabledDates] = useState(new Set<string>());
    const [availableCouponCount, setAvailableCouponCount] = useState(0);
    const [allDynamicPrices, setAllDynamicPrices] = useState<any[]>([]);
    const [isCalculatingDisabledDates, setIsCalculatingDisabledDates] = useState(false);

    const { addToCart } = useCartContext();
    // Fetch inventory stats
    useEffect(() => {
        if (!menuDetail?.enable_inventory_control || !selectedShop || !date) {
            setInventoryStats(null);
            return;
        }
        let active = true;
        server.get('setmenu-inventory-configs', {
            filters: {
                setmenu: id,
                shop: selectedShop,
                date: date.dayString,
                active: true,
            },
            populate: ['shop'],
        }).then((r) => { if (active) setInventoryStats(r.data ?? null); })
            .catch((e) => { console.error(e); if (active) setInventoryStats(null); });
        return () => { active = false; };
    }, [selectedShop, date, id, menuDetail?.enable_inventory_control]);

    // Fetch dynamic price
    useEffect(() => {
        const fetchDynamicPrice = async () => {
            if (date && date.dayString) {
                try {
                    console.log('获取动态价格:', { setmenuId: id, date: date.dayString });

                    const result = await server.get(
                        `setmenu-dynamic-prices/app/${id}/${date.dayString}`,
                    );

                    console.log('动态价格响应:', result);
                    console.log('当前menuDetail价格:', menuDetail?.price);

                    setDynamicPriceData(result.data);

                    if (result.useDefaultPrice) {
                        // 使用默认价格
                        console.log('使用默认价格:', menuDetail?.price);
                        setCurrentPrice(menuDetail?.price);
                        setIsDynamicPrice(false);
                    } else if (result.data) {
                        // 使用动态价格
                        console.log('使用动态价格:', result.data.price);
                        setCurrentPrice(result.data.price);
                        setIsDynamicPrice(true);
                    }
                } catch (error) {
                    console.error('获取动态价格失败:', error);
                    // 出错时使用默认价格
                    setCurrentPrice(menuDetail?.price);
                    setIsDynamicPrice(false);
                    setDynamicPriceData(null);
                }
            } else {
                // 没有选择日期时使用默认价格
                setCurrentPrice(menuDetail?.price);
                setIsDynamicPrice(false);
                setDynamicPriceData(null);
            }
        };

        fetchDynamicPrice();
    }, [date, id, menuDetail?.price]);

    // District change handler
    const handleDistrictChange = (district: string) => {
        setSelectedDistrict(district);
        setFilteredShops(shopList.filter((s) => s.district === district));
        setSelectedShop(null);
        setDate(null);
        setTime("");
        setVisible(false);
    };

    // Available slots
    const availableSlots = useMemo(() => {
        let allDates = _.flatMap(menuDetail?.time_slots, (v) => v?.dates ?? []).sort();
        if (menuDetail?.booking_restriction?.enabled) {
            const today = new Date();
            const advance = menuDetail.booking_restriction.advance_days || 0;
            allDates = allDates.filter((d) => Math.ceil((new Date(d).getTime() - today.getTime()) / 86400000) >= advance);
        }
        return allDates;
    }, [menuDetail?.time_slots, menuDetail?.booking_restriction]);

    // Base data for disabled dates
    useEffect(() => {
        if (!id) return;
        let active = true;
        Promise.all([
            server.get('user-campaign-setmenus', {
                filters: {
                    setmenu: id,
                    email: { $null: true },
                    phone: { $null: true },
                },
            }),
            server.get('setmenu-dynamic-prices', {
                filters: {
                    setmenu: id,
                    active: true,
                },
            }),
        ]).then(([coupons, prices]) => {
            if (!active) return;
            setAvailableCouponCount(coupons.data?.length ?? 0);
            setAllDynamicPrices(prices.data ?? []);
        }).catch(console.error);
        return () => { active = false; };
    }, [id]);

    // Calculate disabled dates
    useEffect(() => {
        const calculateDisabledDates = async () => {
            if (!availableSlots.length) return;

            setIsCalculatingDisabledDates(true);
            // 如果日期选择器正在显示，先关闭它
            if (visible) {
                setVisible(false);
            }
            const disabled = new Set<string>();

            for (const dateString of availableSlots) {
                let shouldDisable = false;

                // 查找该日期的动态价格配置
                const dynamicPriceForDate = allDynamicPrices.find(
                    price =>
                        price.attributes.dates &&
                        price.attributes.dates.includes(dateString),
                );

                if (dynamicPriceForDate) {
                    // 条件1: 动态价格券号售空禁用
                    // 检查该动态价格配置绑定的券号是否用完
                    if (
                        dynamicPriceForDate.bound_coupon_count >=
                        dynamicPriceForDate.required_coupon_count &&
                        dynamicPriceForDate.required_coupon_count > 0
                    ) {
                        console.log(
                            `日期${dateString}被禁用：动态价格券号已售空 (${dynamicPriceForDate.bound_coupon_count}/${dynamicPriceForDate.required_coupon_count})`,
                        );
                        shouldDisable = true;
                    }
                } else {
                    // 条件3: 普通券号售空禁用
                    // 没有动态价格配置，检查普通券号（未绑定到动态价格的券号）
                    const unboundCoupons = await server.get('user-campaign-setmenus', {
                        filters: {
                            setmenu: id,
                            email: { $null: true },
                            phone: { $null: true },
                            $or: [{ is_bound: { $null: true } }, { is_bound: false }],
                        },
                    });

                    if (!unboundCoupons.data || unboundCoupons.data.length === 0) {
                        console.log(
                            `日期${dateString}被禁用：无动态价格配置且普通券号已售空`,
                        );
                        shouldDisable = true;
                    }
                }

                // 条件2: 检查库存配置
                if (
                    !shouldDisable &&
                    menuDetail?.enable_inventory_control &&
                    selectedShop
                ) {
                    try {
                        const response = await server.get('setmenu-inventory-configs', {
                            filters: {
                                setmenu: id,
                                shop: selectedShop,
                                date: dateString,
                                active: true,
                            },
                        });

                        if (response.data && response.data.length > 0) {
                            // 检查是否所有时间段库存都用完
                            const allTimeSlotsExhausted = response.data.every((config: { attributes: { used_quantity: any; max_quantity: any; }; used_quantity: any; max_quantity: any; }) => {
                                // 兼容不同的数据结构
                                const usedQuantity =
                                    config.attributes?.used_quantity ?? config.used_quantity ?? 0;
                                const maxQuantity =
                                    config.attributes?.max_quantity ?? config.max_quantity ?? 0;
                                return usedQuantity >= maxQuantity;
                            });

                            if (allTimeSlotsExhausted) {
                                console.log(`日期${dateString}被禁用：所有时间段库存已用完`);
                                shouldDisable = true;
                            }
                        }
                    } catch (error) {
                        console.error(`检查日期${dateString}库存配置失败:`, error);
                    }
                }

                if (shouldDisable) {
                    disabled.add(dateString);
                }
            }

            setDisabledDates(disabled);
            setIsCalculatingDisabledDates(false);
        };

        calculateDisabledDates();
    }, [
        availableSlots,
        allDynamicPrices,
        availableCouponCount,
        selectedShop,
        menuDetail?.enable_inventory_control,
        id,
    ]);

    const filteredAvailableSlots = useMemo(
        () => availableSlots.filter((d) => !disabledDates.has(d)),
        [availableSlots, disabledDates]
    );

    // Timeslots
    const timeslots = useMemo(() => {
        if (!date) return [];
        const slot = _.find(menuDetail?.time_slots, (v) => _.includes(v?.dates, date.dayString));
        if (!slot) return [];
        const startAt = timeToNumber(slot.startAt);
        const endAt = timeToNumber(slot.endAt);
        const range = _.range(startAt, endAt, slot.interval);
        const now = new Date();
        const currentMins = now.getHours() * 60 + now.getMinutes();
        const todayISO = now.toISOString().split("T")[0];
        const filtered = date.dayString === todayISO ? range.filter((t) => t >= currentMins) : range;

        return filtered.map((v) => {
            const timeString = numberToTime(v);
            if (menuDetail?.enable_inventory_control) {
                if (!inventoryStats?.length) return null;
                const config = inventoryStats.find((c) => c.time_start?.substring(0, 5) === timeString);
                if (!config) return null;
                const remaining = config.max_quantity - config.used_quantity;
                if (remaining <= 0) return null;
                return menuDetail?.show_remaining_quantity ? `${timeString} (剩餘${remaining}份)` : timeString;
            }
            return timeString;
        }).filter(Boolean) as string[];
    }, [date, inventoryStats, menuDetail?.time_slots, menuDetail?.enable_inventory_control, menuDetail?.show_remaining_quantity]);

    // Add to cart
    const addCart = useCallback(async () => {
        if (!selectedShop) { showToast("請選擇店鋪", "error"); return; }
        if (!date) { showToast("請選擇日期", "error"); return; }
        if (!time) { showToast("請選擇時間", "error"); return; }
        try {
            // await axios.post(`${SERVER_URL}/cart-setmenu-items`, {
            //     data: {
            //         setmenu: id,
            //         shop: selectedShop,
            //         booking_date: date.dayString,
            //         booking_time: `${time}:00`,
            //         quantity,
            //         remark,
            //         dynamic_price_id: isDynamicPrice ? dynamicPriceData?.dynamicPriceId : null,
            //         dynamic_price: isDynamicPrice ? currentPrice : null,
            //     },
            // });
            const selectedShopEntity = menuDetail?.shops?.data?.find((v) => v.id === selectedShop);

            console.log(
                {
                    id: Date.now(),
                    attributes: {
                        setmenu: {
                            data: {
                                id: id ?? undefined,
                                attributes: menuDetail,
                            },
                        },
                        shop: {
                            data: {
                                id: selectedShop,
                                attributes: selectedShopEntity?.attributes ?? {},
                            },
                        },
                        booking_date: date.dayString,
                        booking_time: `${time}:00`,
                        quantity,
                        remark,
                        dynamic_price_id: isDynamicPrice ? dynamicPriceData?.dynamicPriceId : undefined,
                        dynamic_price: isDynamicPrice ? currentPrice : undefined,
                    }
                }
            )

            addToCart({
                id: Date.now(),
                attributes: {
                    setmenu: {
                        data: {
                            id: id ?? undefined,
                            attributes: menuDetail,
                        },
                    },
                    shop: {
                        data: {
                            id: selectedShop,
                            attributes: selectedShopEntity?.attributes ?? {},
                        },
                    },
                    booking_date: date.dayString,
                    booking_time: `${time}:00`,
                    quantity,
                    remark,
                    dynamic_price_id: isDynamicPrice ? dynamicPriceData?.dynamicPriceId : undefined,
                    dynamic_price: isDynamicPrice ? currentPrice : undefined,
                },
            });
            showToast("成功加入購物車", "success");
            setTimeout(() => navigate(`/${companyParam}/setmenulist`), 1200);
        } catch (e: any) {
            showToast(e?.response?.data?.error?.message ?? e?.message ?? "加入購物車失敗", "error");
        }
    }, [selectedShop, date, time, quantity, remark, id, isDynamicPrice, dynamicPriceData, currentPrice, navigate, addToCart, companyParam, menuDetail]);

    const totalPrice = (currentPrice ?? menuDetail?.price ?? 0) * quantity;

    return (
        <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", width: "100%" }}>
            <ToastMessage toast={toast} onClose={() => setToast(null)} />
            <div style={{ flex: 1, width: "100%" }}>
                <div
                    style={{
                        width: "100%",
                        maxWidth: CONTENT_MAX_WIDTH,
                        margin: "0 auto",
                        padding: 16,
                        paddingBottom: 160,
                        boxSizing: "border-box",
                    }}
                >

                    {/* District */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={styles.label}>店鋪地區</div>
                        <select
                            className="form-select"
                            style={styles.select}
                            value={selectedDistrict}
                            onChange={(e) => handleDistrictChange(e.target.value)}
                        >
                            <option value="">選擇地區</option>
                            {districts.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </select>
                    </div>

                    {/* Shop */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={styles.label}>店鋪</div>
                        <select
                            className="form-select"
                            style={{ ...styles.select, opacity: !selectedDistrict ? 0.5 : 1 }}
                            disabled={!selectedDistrict}
                            value={selectedShop ?? ""}
                            onChange={(e) => {
                                setSelectedShop(Number(e.target.value) || null);
                                setDate(null);
                                setTime("");
                                setVisible(false);
                            }}
                        >
                            <option value="">選擇店鋪</option>
                            {filteredShops.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                    </div>

                    {/* Date picker */}
                    <div style={{ marginBottom: 16 }}>
                        <div>請選擇日期:</div>
                        <button
                            type="button"
                            disabled={isCalculatingDisabledDates}
                            onClick={() => !isCalculatingDisabledDates && setVisible(true)}
                            style={{
                                ...styles.input,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: isCalculatingDisabledDates ? "not-allowed" : "pointer",
                                opacity: isCalculatingDisabledDates ? 0.6 : 1,
                                textAlign: "left",
                            }}
                        >
                            <span style={{ color: isCalculatingDisabledDates ? "#999" : "#000" }}>
                                {isCalculatingDisabledDates
                                    ? "正在檢查日期可用性..."
                                    : date
                                        ? `${date.dayString} (星期${DAY_MAP[new Date(date.dayString).getDay()]})`
                                        : "請選擇日期"}
                            </span>
                            {isCalculatingDisabledDates
                                ? <AiOutlineClockCircle color="#999" size={24} />
                                : <AiOutlineCalendar color="black" size={24} />}
                        </button>
                    </div>

                    {/* Time */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={styles.label}>請選擇時間:</div>
                        <select
                            className="form-select"
                            style={styles.select}
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                        >
                            <option value="">請選擇時間</option>
                            {timeslots.map((t) => {
                                const timeOnly = t.split(" ")[0];
                                return <option key={t} value={timeOnly}>{t}</option>;
                            })}
                        </select>
                    </div>

                    {/* Remark */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={styles.label}>備註</div>
                        <textarea
                            style={{ ...styles.input, minHeight: 80, resize: "vertical" }}
                            value={remark}
                            onChange={(e) => setRemark(e.target.value)}
                        />
                    </div>

                    {/* Quantity */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={styles.label}>請選擇數量：</div>
                        <div style={{ maxWidth: "50%" }}>
                            <QuantityButton
                                quantity={quantity}
                                setQuantity={setQuantity}
                                addDisabled={
                                    menuDetail?.user_max_quantity
                                        ? quantity >= counting || quantity >= menuDetail.user_max_quantity || quantity >= (menuDetail?.user_max_checkout_quantity ?? Infinity)
                                        : false
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div
                className="bg-bg"
                style={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    borderTop: "1px solid #CDCFD0",
                    padding: 16,
                }}>
                <div
                    style={{
                        width: "100%",
                        maxWidth: CONTENT_MAX_WIDTH,
                        margin: "0 auto",
                    }}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <div style={{ fontWeight: "700", fontSize: 16 }}>總計金額</div>
                        <div style={{ color: "var(--bs-primary)", fontSize: 18, fontWeight: "700" }}>{`$ ${totalPrice}`}</div>
                    </div>
                    <button
                        type="button"
                        className="btn-primary btn"
                        onClick={() => void addCart()}
                        style={{
                            width: "100%",
                            paddingTop: 16,
                            paddingBottom: 16,
                            alignItems: "center",
                            justifyContent: "space-around",
                            borderRadius: 50,
                            backgroundColor: "var(--bs-primary)",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "row",
                            color: "white",
                            fontSize: 16,
                            fontWeight: "500",
                        }}
                    >
                        <div style={{ width: 24 }} />
                        <span>加入購物車</span>
                        <AiOutlineShoppingCart color="white" size={24} />
                    </button>
                </div>
            </div>

            {/* Calendar Modal */}
            <CalendarModal
                key={`cal-${selectedShop}-${filteredAvailableSlots.length}-${isCalculatingDisabledDates}`}
                visible={visible && !isCalculatingDisabledDates}
                close={() => setVisible(false)}
                setDate={setDate}
                date={date}
                availableDates={filteredAvailableSlots}
            />

        </div >
    );
}